import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Upload, FileText, Clock, CheckCircle, XCircle,
  Copy, Download, Trash2, Mic, RefreshCw
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Document, Paragraph, TextRun, Packer } from 'docx';
import html2pdf from 'html2pdf.js';

interface TranscriptionRecord {
  id: string;
  created_at: string;
  file_name: string;
  storage_path: string;
  file_size_bytes: number | null;
  status: 'pending' | 'processing' | 'done' | 'error';
  transcript: string | null;
  error_message: string | null;
}

const ACCEPTED_TYPES = '.mp3,.mp4,.m4a,.wav,.ogg,.webm';
const MAX_FILE_MB = 200;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function StatusBadge({ status }: { status: TranscriptionRecord['status'] }) {
  const map = {
    pending: { label: 'Pending', classes: 'bg-orange-10 text-orange-50', icon: Clock },
    processing: { label: 'Processing', classes: 'bg-brand-10 text-brand-60', icon: RefreshCw },
    done: { label: 'Done', classes: 'bg-success-10 text-success-50', icon: CheckCircle },
    error: { label: 'Error', classes: 'bg-error-10 text-error-50', icon: XCircle },
  };
  const { label, classes, icon: Icon } = map[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${classes}`}>
      <Icon className={`w-3.5 h-3.5 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {label}
    </span>
  );
}

async function downloadAsPdf(fileName: string, transcript: string) {
  const container = document.createElement('div');
  container.style.cssText = 'font-family: Georgia, serif; font-size: 14px; line-height: 1.7; padding: 32px; color: #1a1a1a;';
  container.innerHTML = `<h1 style="font-size:20px;margin-bottom:16px;">${fileName} — Transcript</h1><p style="white-space:pre-wrap;">${transcript.replace(/</g, '&lt;')}</p>`;
  document.body.appendChild(container);
  await html2pdf().set({ margin: 20, filename: `${fileName.replace(/\.[^.]+$/, '')}_transcript.pdf`, jsPDF: { unit: 'mm', format: 'a4' } }).from(container).save();
  document.body.removeChild(container);
}

async function downloadAsDocx(fileName: string, transcript: string) {
  const doc = new Document({
    sections: [{
      children: [
        new Paragraph({ children: [new TextRun({ text: `${fileName} — Transcript`, bold: true, size: 28 })] }),
        new Paragraph({}),
        ...transcript.split('\n').map(line => new Paragraph({ children: [new TextRun({ text: line })] })),
      ],
    }],
  });
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName.replace(/\.[^.]+$/, '')}_transcript.docx`;
  a.click();
  URL.revokeObjectURL(url);
}

function openInGoogleDocs(transcript: string) {
  const encoded = encodeURIComponent(transcript);
  window.open(`https://docs.google.com/document/create?body=${encoded}`, '_blank');
}

export function AudioTranscriber() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<TranscriptionRecord[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const loadRecords = useCallback(async () => {
    const { data, error } = await (supabase as any)
      .from('audio_transcriptions')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setRecords(data as TranscriptionRecord[]);
  }, []);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    (async () => {
      const isAdmin = await isUserAdmin(user.id);
      if (!isAdmin) { navigate('/login'); return; }
      await loadRecords();
      setIsLoading(false);
    })();
  }, [user, navigate, loadRecords]);

  // Poll while any records are processing
  useEffect(() => {
    const hasProcessing = records.some(r => r.status === 'pending' || r.status === 'processing');
    if (hasProcessing && !pollingRef.current) {
      pollingRef.current = setInterval(loadRecords, 3000);
    } else if (!hasProcessing && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    };
  }, [records, loadRecords]);

  const processFile = async (file: File) => {
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      showToast(`File too large. Max size is ${MAX_FILE_MB} MB.`);
      return;
    }

    setUploading(true);
    try {
      const storagePath = `${user!.id}/${Date.now()}_${file.name}`;

      const { error: uploadErr } = await supabase.storage
        .from('audio-uploads')
        .upload(storagePath, file, { contentType: file.type });

      if (uploadErr) throw new Error(`Upload failed: ${uploadErr.message}`);

      const { data: record, error: insertErr } = await (supabase as any)
        .from('audio_transcriptions')
        .insert({
          file_name: file.name,
          storage_path: storagePath,
          file_size_bytes: file.size,
          uploaded_by: user!.id,
        })
        .select()
        .single();

      if (insertErr || !record) throw new Error('Failed to create transcription record');

      await loadRecords();

      // Invoke edge function (fire-and-forget from client perspective)
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      supabase.functions.invoke('transcribe-audio', {
        body: { transcriptionId: record.id },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      }).then(() => loadRecords()).catch(console.error);

      showToast('Upload complete. Transcription in progress...');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      showToast(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      await processFile(file);
    }
  };

  const handleDelete = async (record: TranscriptionRecord) => {
    await supabase.storage.from('audio-uploads').remove([record.storage_path]);
    await (supabase as any).from('audio_transcriptions').delete().eq('id', record.id);
    setRecords(prev => prev.filter(r => r.id !== record.id));
    if (selectedId === record.id) setSelectedId(null);
  };

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (isLoading) return <LoadingSpinner message="Loading Audio Transcriber..." />;

  const selectedRecord = records.find(r => r.id === selectedId) ?? null;

  return (
    <div className="min-h-screen bg-brand-5">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="p-2 text-gray-40 hover:bg-gray-10 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="p-2.5 bg-brand-90 rounded-xl shadow-soft">
              <Mic className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-heading-xs text-gray-80">Audio Transcriber</h1>
              <p className="text-text-sm text-gray-40">Upload audio, get transcripts</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Upload zone */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className={`freud-card-elevated mb-8 p-8 border-2 border-dashed transition-colors cursor-pointer ${
            dragOver ? 'border-brand-50 bg-brand-5' : 'border-gray-20 bg-white'
          }`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            className="hidden"
            onChange={e => handleFiles(e.target.files)}
          />
          <div className="flex flex-col items-center gap-4 text-center">
            {uploading ? (
              <>
                <div className="w-14 h-14 bg-brand-10 rounded-full flex items-center justify-center">
                  <RefreshCw className="w-7 h-7 text-brand-60 animate-spin" />
                </div>
                <p className="text-text-base text-gray-60 font-medium">Uploading...</p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 bg-brand-10 rounded-full flex items-center justify-center">
                  <Upload className="w-7 h-7 text-brand-60" />
                </div>
                <div>
                  <p className="text-text-base text-gray-80 font-semibold mb-1">Drop audio files here or click to browse</p>
                  <p className="text-text-sm text-gray-40">Supports MP3, MP4, M4A, WAV, OGG, WEBM &mdash; up to {MAX_FILE_MB} MB</p>
                </div>
              </>
            )}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* File list */}
          <div className="lg:col-span-2">
            <h2 className="text-text-xl text-gray-80 mb-4">Transcriptions ({records.length})</h2>
            {records.length === 0 ? (
              <div className="freud-card-elevated p-8 text-center">
                <FileText className="w-10 h-10 text-gray-30 mx-auto mb-3" />
                <p className="text-text-sm text-gray-40">No files yet. Upload audio above to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {records.map(record => (
                  <motion.div
                    key={record.id}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`freud-card-elevated p-4 cursor-pointer transition-all ${
                      selectedId === record.id ? 'ring-2 ring-brand-50' : 'hover:shadow-soft-lg'
                    }`}
                    onClick={() => setSelectedId(record.id === selectedId ? null : record.id)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-text-sm text-gray-80 font-medium truncate flex-1">{record.file_name}</p>
                      <button
                        onClick={e => { e.stopPropagation(); handleDelete(record); }}
                        className="p-1 text-gray-30 hover:text-error-50 rounded transition-colors flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <StatusBadge status={record.status} />
                      {record.file_size_bytes && (
                        <span className="text-text-xs text-gray-40">{formatBytes(record.file_size_bytes)}</span>
                      )}
                      <span className="text-text-xs text-gray-30">{formatDate(record.created_at)}</span>
                    </div>
                    {record.status === 'error' && record.error_message && (
                      <p className="mt-2 text-text-xs text-error-50 truncate">{record.error_message}</p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Transcript panel */}
          <div className="lg:col-span-3">
            <h2 className="text-text-xl text-gray-80 mb-4">Transcript</h2>
            <AnimatePresence mode="wait">
              {!selectedRecord ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="freud-card-elevated p-8 text-center min-h-64 flex flex-col items-center justify-center"
                >
                  <Mic className="w-10 h-10 text-gray-30 mx-auto mb-3" />
                  <p className="text-text-sm text-gray-40">Select a transcription from the list to view the transcript.</p>
                </motion.div>
              ) : selectedRecord.status !== 'done' ? (
                <motion.div
                  key="waiting"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="freud-card-elevated p-8 text-center min-h-64 flex flex-col items-center justify-center"
                >
                  {selectedRecord.status === 'processing' || selectedRecord.status === 'pending' ? (
                    <>
                      <RefreshCw className="w-10 h-10 text-brand-50 animate-spin mx-auto mb-3" />
                      <p className="text-text-sm text-gray-60 font-medium">Transcribing audio...</p>
                      <p className="text-text-xs text-gray-40 mt-1">This may take a minute for longer files.</p>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-10 h-10 text-error-50 mx-auto mb-3" />
                      <p className="text-text-sm text-error-50 font-medium">Transcription failed</p>
                      {selectedRecord.error_message && (
                        <p className="text-text-xs text-gray-40 mt-1">{selectedRecord.error_message}</p>
                      )}
                    </>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key={selectedRecord.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="freud-card-elevated p-6"
                >
                  {/* Export actions */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    <button
                      onClick={() => handleCopy(selectedRecord.id, selectedRecord.transcript!)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-10 hover:bg-gray-20 text-text-sm text-gray-60 font-medium transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                      {copiedId === selectedRecord.id ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      onClick={() => downloadAsPdf(selectedRecord.file_name, selectedRecord.transcript!)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-error-5 hover:bg-error-10 text-text-sm text-error-50 font-medium transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </button>
                    <button
                      onClick={() => downloadAsDocx(selectedRecord.file_name, selectedRecord.transcript!)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-5 hover:bg-brand-10 text-text-sm text-brand-60 font-medium transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Word (.docx)
                    </button>
                    <button
                      onClick={() => openInGoogleDocs(selectedRecord.transcript!)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-success-5 hover:bg-success-10 text-text-sm text-success-50 font-medium transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      Google Docs
                    </button>
                  </div>

                  {/* Transcript text */}
                  <div className="bg-gray-5 rounded-xl p-5 max-h-[520px] overflow-y-auto">
                    <p className="text-text-sm text-gray-70 leading-relaxed whitespace-pre-wrap">
                      {selectedRecord.transcript}
                    </p>
                  </div>

                  <p className="mt-3 text-text-xs text-gray-30">
                    {selectedRecord.transcript!.split(/\s+/).filter(Boolean).length} words
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-80 text-white text-text-sm px-5 py-3 rounded-xl shadow-soft-lg z-50"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
